import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Taroko Gorge and the coast up to Qixingtan (taroko package).
 *
 * West → east along the Liwu River (立霧溪), which runs through a marble canyon to the Pacific:
 *   Cimu Bridge · Tunnel of Nine Turns · Swallow Grotto (helmet zone 'gorge') ·
 *   Eternal Spring Shrine (north cliff) + bell tower · Buluowan terrace (south) ·
 *   Shakadang Trail (clear side stream) · Visitor centre + shuttle stop · Taroko Arch ·
 *   coastal highway: north to the Qingshui Cliffs, south to Qixingtan's pebble crescent.
 *
 * Ground chars of this package: 'x' marble paving / trail (Qixingtan's grey-blue pebbles inside the
 * beach rect), '!' cycle path, '?' forest (solid; inside the Shakadang stream rect it is the clear stream).
 */
const W = 100, H = 48;
const b = new MapBuilder(W, H, 'g');

// --- North mountains: forest on the ridges, marble cliffs down to the river -------------------
b.fill(0, 0, 68, 7, '?').fill(0, 7, 74, 7, 'M');
// Qingshui: bare cliffs all the way to the sea.
b.fill(68, 0, 12, 14, 'M');
// --- The Liwu River ---------------------------------------------------------------------------
b.fill(0, 14, 88, 5, 'q');
// Swallow Grotto: the canyon pinches, the cliff overhangs the river.
b.fill(22, 14, 18, 1, 'M').fill(22, 18, 18, 1, 'x');
// River mouth: a little wider, with pebble bars.
b.fill(76, 13, 12, 1, 'q').fill(76, 19, 11, 1, 'x');
// --- South bank: walkway along the river and the Central Cross-Island Highway -----------------
b.fill(0, 19, 66, 1, 'x').fill(0, 20, 68, 2, 'r');
// Gorge road turns south through the Taroko Arch, then east to the coast.
b.fill(66, 20, 2, 14, 'r').fill(66, 32, 10, 2, 'r');
// South cliff under the road, deeper around the Tunnel of Nine Turns, forest behind.
b.fill(0, 22, 56, 3, 'M').fill(0, 22, 24, 10, 'M');
b.fill(0, 32, 24, 16, '?').fill(24, 41, 32, 7, '?').fill(46, 22, 10, 26, 'M');
// --- Cimu Bridge (慈母橋): red bridge north across the river to the pavilion ledge ------------
b.fill(0, 10, 9, 4, 'x').fill(2, 14, 2, 5, 'x');
// --- Tunnel of Nine Turns (九曲洞): a trail that zig-zags through the marble -------------------
const zig: [number, number, number, number][] = [[6, 22, 2, 9], [6, 29, 7, 2], [11, 25, 2, 6], [11, 25, 7, 2], [16, 25, 2, 6], [16, 29, 6, 2], [20, 22, 2, 9]];
for (const [x, y, w, h] of zig) b.fill(x, y, w, h, 'x');
b.fill(8, 22, 3, 2, 'x'); // a notch for the trail board
// --- Eternal Spring Shrine (長春祠): ledge on the north cliff, stairs up to the bell tower -----
b.fill(41, 11, 10, 3, 'x').fill(43, 14, 2, 5, 'x');
b.fill(41, 5, 2, 6, 'x').fill(40, 2, 7, 3, 'x');
// --- Buluowan (布洛灣): the Truku terrace above the south cliff ---------------------------------
b.fill(42, 22, 2, 3, 'x');
b.fill(24, 25, 22, 16, 'g');
b.fill(25, 30, 20, 2, 'p').fill(34, 25, 2, 16, 'p').fill(42, 25, 2, 6, 'p');
b.fill(26, 34, 6, 5, 'F'); // millet field
// --- Shakadang Trail (砂卡礑步道): clear stream (?) in a marble valley, trail cut in the west wall
b.fill(51, 22, 3, 23, '?');
b.fill(49, 22, 2, 21, 'x');
b.fill(45, 22, 4, 2, 'x').fill(48, 24, 1, 3, 'x'); // trailhead with the Truku snack stall
b.fill(51, 38, 3, 1, 'y'); // little suspension bridge
b.fill(54, 36, 2, 5, 'x');
// --- Visitor centre, shuttle stop and the coach park -------------------------------------------
b.fill(56, 22, 10, 10, 'x');
b.building(57, 23, 8, 5, { roof: 'Y', wall: 'B', window: 'w', roofRows: 3, doors: [], windows: [0, 1, 2, 5, 6, 7] });
b.fill(56, 34, 10, 1, 's').fill(56, 32, 10, 2, 'r');
// --- Coastal highway (台9線) north to Qingshui and south to Qixingtan --------------------------
b.fill(74, 0, 2, H, 'r');
b.fill(76, 4, 3, 4, 'x'); // Qingshui lookout pull-out
// --- Pacific Ocean and Qixingtan (七星潭) -------------------------------------------------------
/** First sea tile of row `y`: the Qingshui cliffs, the river mouth, then Qixingtan's crescent. */
export const shoreX = (y: number) => (y < 13 ? 80 : y < 20 ? 87 : 90 - Math.round(4 * Math.sin((Math.PI * (y - 19)) / 30)));
for (let y = 0; y < H; y++) {
  const shore = shoreX(y);
  b.fill(shore, y, W - shore, 1, 'Z');
  if (y >= 20) {
    b.fill(76, y, 2, 1, 'g').fill(78, y, 2, 1, '!').fill(80, y, shore - 80, 1, 'x');
  }
}
// The whale-watching pier runs out from the south end of the beach.
b.fill(86, 44, 8, 1, 'x');
// --- Qixingtan village: lanes, the bonito museum, houses, the Amis dance square -----------------
b.fill(56, 35, 18, 13, 'g');
b.fill(56, 39, 18, 2, 's').fill(64, 35, 2, 13, 's').fill(72, 35, 2, 13, 's');
b.building(57, 35, 7, 4, { roof: 'U', wall: 'B', window: 'w', roofRows: 2, doors: [], windows: [1, 5] }); // bonito museum
b.building(66, 35, 6, 4, { roof: 'R', wall: 'K', window: 'v', roofRows: 2, doors: [], windows: [1, 4] });
b.fill(57, 42, 7, 5, 'x'); // Amis dance square
b.building(66, 42, 6, 4, { roof: 'R', wall: 'B', window: 'w', roofRows: 2, doors: [], windows: [1, 4] });
b.fill(56, 47, 18, 1, 'h');

// --- Decor ------------------------------------------------------------------------------------
const mid = (tx: number, ty: number) => ({ x: px(tx) + 8, y: px(ty) + 14 });
const tree = (tx: number, ty: number, kind: 'tree' | 'pine' | 'cherry' = 'tree'): Decor => ({ kind, x: px(tx) + 8, y: px(ty) + 14, solid: { x: px(tx) + 2, y: px(ty) + 8, w: 12, h: 6 } });
/** Wide decor is anchored at its centre (the prop culling assumes it). */
const span = (tx: number, tw: number) => ({ x: px(tx) + px(tw) / 2, w: px(tw) });
const solidAt = (tx: number, ty: number, tw: number, h = 8) => ({ x: px(tx) + 1, y: px(ty + 1) - h, w: px(tw) - 2, h });

const decor: Decor[] = [
  // Canyon walls first, so the swallow holes, nets and waterfalls draw over them.
  // Tall marble faces (cached bitmaps) over the north cliff: 100–112 px high so the canyon towers.
  ...([[9, 6, 14], [15, 7, 14], [22, 6, 15], [28, 6, 15], [34, 6, 15], [51, 6, 14], [57, 6, 14], [63, 5, 14], [68, 6, 14], [76, 4, 13]] as const).map(
    ([tx, tw, ty], i): Decor => ({ kind: 'tk_marbleWall', ...span(tx, tw), y: px(ty), h: ty === 15 ? 112 : 100, variant: i % 4 }),
  ),
  { kind: 'tk_marbleWall', ...span(0, 9), y: px(10), h: 60, variant: 1 },
  { kind: 'tk_marbleWall', ...span(43, 8), y: px(11), h: 64, variant: 2 },
  ...[5, 9, 13].map((ty): Decor => ({ kind: 'tk_eastCliff', x: px(80), y: px(ty), h: px(ty === 5 ? 5 : 4) })),
  // Cimu Bridge: red railings and marble lions (the pavilion is the tk_cimu object).
  { kind: 'tk_cimuBridge', x: px(3), y: px(19), w: px(2), h: px(5) },
  { kind: 'tk_lion', ...mid(1, 19), solid: solidAt(1, 19, 1, 6) },
  { kind: 'tk_lion', ...mid(4, 19), variant: 1, solid: solidAt(4, 19, 1, 6) },
  { kind: 'pine', ...mid(0, 10) },
  { kind: 'tk_boulder', ...mid(1, 12), variant: 1 },
  // Tunnel of Nine Turns: rock arches over each north-south leg, rock-fall nets and warnings.
  { kind: 'tunnel', x: px(7), y: px(26), w: px(2) },
  { kind: 'tunnel', x: px(12), y: px(28), w: px(2) },
  { kind: 'tunnel', x: px(17), y: px(28), w: px(2) },
  { kind: 'tunnel', x: px(21), y: px(26), w: px(2) },
  { kind: 'tk_rockWarn', x: px(34) + 8, y: px(18) + 8, solid: solidAt(34, 18, 1, 4) },
  { kind: 'tk_fallNet', ...span(9, 11), y: px(14) },
  { kind: 'tk_fallNet', ...span(55, 9), y: px(14) },
  { kind: 'tk_fallNet', ...span(12, 4), y: px(22), variant: 1 },
  // Swallow Grotto: the cliff full of nesting holes, and the swallows themselves.
  { kind: 'tk_swallowCliff', ...span(22, 18), y: px(15) },
  { kind: 'tk_birds', ...span(23, 9), y: px(15) },
  { kind: 'tk_birds', ...span(31, 9), y: px(15), variant: 1 },
  // Railings along the riverside walkway.
  { kind: 'tk_rail', ...span(5, 17), y: px(19) + 2 },
  { kind: 'tk_rail', ...span(22, 18), y: px(18) + 2 },
  { kind: 'tk_rail', ...span(40, 3), y: px(19) + 2 },
  { kind: 'tk_rail', ...span(45, 6), y: px(19) + 2 },
  { kind: 'tk_rail', ...span(54, 12), y: px(19) + 2 },
  // Eternal Spring Shrine (the tk_shrine object): its red bridge, lanterns, a second cascade.
  { kind: 'tk_redBridge', x: px(44), y: px(19), w: px(2), h: px(5) },
  { kind: 'waterfall', x: px(54) + 6, y: px(14) + 4, h: 70 },
  { kind: 'tk_lantern', ...mid(42, 11) },
  { kind: 'tk_lantern', ...mid(50, 11) },
  { kind: 'pine', ...mid(39, 3) },
  { kind: 'pine', ...mid(47, 3) },
  { kind: 'tk_boulder', ...mid(40, 15), variant: 2 },
  // Buluowan terrace: Truku houses, millet racks, a fire pit; rock monkeys on the cliff edge.
  { kind: 'tk_trukuHouse', x: px(28), y: px(29) + 12, solid: { x: px(25) + 4, y: px(27) + 4, w: px(6) - 8, h: px(2) } },
  { kind: 'tk_trukuHouse', x: px(40), y: px(29) + 12, variant: 1, solid: { x: px(37) + 4, y: px(27) + 4, w: px(6) - 8, h: px(2) } },
  { kind: 'tk_trukuHouse', x: px(41), y: px(38) + 12, variant: 2, solid: { x: px(38) + 4, y: px(36) + 4, w: px(6) - 8, h: px(2) } },
  { kind: 'tk_milletRack', ...mid(33, 35), solid: solidAt(32, 35, 2, 5) },
  { kind: 'tk_milletRack', ...mid(30, 39), variant: 1, solid: solidAt(29, 39, 2, 5) },
  { kind: 'tk_firePit', ...mid(38, 33) },
  { kind: 'tk_monkey', x: px(25) + 4, y: px(24) + 10 },
  { kind: 'tk_monkey', x: px(30), y: px(22) + 6, variant: 1 },
  { kind: 'tk_monkey', x: px(36) + 6, y: px(24) + 12, variant: 2 },
  { kind: 'tk_monkey', x: px(63), y: px(13) + 10, variant: 1 },
  ...[[24, 26], [24, 32], [45, 34], [25, 39], [36, 39], [44, 40], [29, 25]].map(([tx, ty], i) => tree(tx, ty, i % 2 ? 'tree' : 'pine')),
  // Shakadang: red bridge on the road, marble boulders in the clear stream, a rope bridge.
  { kind: 'tk_redRail', ...span(51, 3), y: px(19) + 2 },
  { kind: 'tk_redRail', ...span(51, 3), y: px(21) + 15 },
  { kind: 'tk_boulder', ...mid(52, 24) },
  { kind: 'tk_boulder', ...mid(51, 28), variant: 1 },
  { kind: 'tk_boulder', ...mid(53, 31), variant: 2 },
  { kind: 'tk_boulder', ...mid(52, 34) },
  { kind: 'tk_boulder', ...mid(51, 42), variant: 1 },
  { kind: 'tk_boulder', ...mid(53, 44), variant: 2 },
  { kind: 'ropeBridgeRails', x: px(51), y: px(38) - 2, w: px(3) },
  // Visitor centre, the Taroko Arch, coaches.
  { kind: 'tk_vcSign', x: px(61), y: px(28) },
  { kind: 'tarokoGate', x: px(67), y: px(29) + 4, w: px(5), solid: { x: px(65) + 4, y: px(29), w: 8, h: 4 } },
  { kind: 'tk_solid', x: px(68) + 6, y: px(29) + 4, solid: { x: px(68) + 2, y: px(29), w: 8, h: 4 } },
  { kind: 'tk_parkStone', x: px(70) + 8, y: px(25) + 12, solid: { x: px(69), y: px(25) + 4, w: 32, h: 8 } },
  { kind: 'tk_tourBus', x: px(58) + 12, y: px(33) + 12, solid: { x: px(56) + 4, y: px(33) - 2, w: px(5) - 4, h: 14 } },
  { kind: 'tk_tourBus', x: px(63) + 12, y: px(33) + 12, variant: 2, solid: { x: px(61) + 4, y: px(33) - 2, w: px(5) - 4, h: 14 } },
  { kind: 'vendingDeco', x: px(56) + 8, y: px(28) + 12, solid: solidAt(56, 28, 1, 6) },
  { kind: 'trashBin', x: px(65) + 8, y: px(22) + 12 },
  { kind: 'tk_bench', x: px(62) + 8, y: px(31) + 14, solid: solidAt(62, 31, 1, 4) },
  ...[[69, 28], [72, 22], [72, 29], [68, 23]].map(([tx, ty], i) => tree(tx, ty, i % 2 ? 'cherry' : 'tree')),
  // Qingshui Cliffs: the highway in tunnels, the lookout, surf at the foot of the cliffs.
  { kind: 'tunnel', x: px(75), y: px(2) + 8, w: px(2) },
  { kind: 'tunnel', x: px(75), y: px(11), w: px(2) },
  { kind: 'tk_bridgeRail', x: px(74), y: px(19), h: px(5) },
  { kind: 'tk_bridgeRail', x: px(76) - 2, y: px(19), h: px(5) },
  { kind: 'tk_rail', ...span(76, 3), y: px(4) + 2, variant: 1 },
  // Qixingtan: palms and lamps along the cycle path, boats on the pebbles, surf.
  { kind: 'tk_boat', x: px(83), y: px(22) + 6, solid: { x: px(81) + 6, y: px(22), w: 40, h: 8 } },
  { kind: 'tk_boat', x: px(85), y: px(39) + 10, variant: 1, solid: { x: px(83) + 6, y: px(39) + 4, w: 40, h: 8 } },
  { kind: 'tk_boatSea', x: px(94), y: px(30) },
  { kind: 'tk_qixingStone', x: px(77), y: px(21) + 12, solid: { x: px(76) + 2, y: px(21) + 4, w: 28, h: 8 } },
  { kind: 'pier', x: px(86), y: px(45) - 2, w: px(8) },
  ...[27, 35, 39, 43].map((ty, i): Decor => ({ kind: 'tk_palm', x: px(76) + 8, y: px(ty) + 14, variant: i % 2, solid: { x: px(76) + 4, y: px(ty) + 9, w: 8, h: 5 } })),
  ...[33, 41, 46].map((ty): Decor => ({ kind: 'lamp', x: px(77) + 8, y: px(ty) + 14 })),
  { kind: 'tk_bench', x: px(77) + 8, y: px(29) + 14, solid: solidAt(77, 29, 1, 4) },
  // Village: shop signs, scooters, lanterns over the dance square.
  { kind: 'tk_sign', x: px(68) + 8, y: px(39), variant: 0 },
  { kind: 'tk_sign', x: px(68) + 8, y: px(46), variant: 1 },
  { kind: 'scooter', x: px(70) + 8, y: px(40) + 12 },
  { kind: 'scooter', x: px(71) + 8, y: px(40) + 12, variant: 1 },
  { kind: 'scooter', x: px(66) + 8, y: px(46) + 12, variant: 2 },
  { kind: 'lanternString', x: px(57), y: px(42) + 4, w: px(7) },
  ...[[56, 41], [71, 46]].map(([tx, ty]) => tree(tx, ty)),
  { kind: 'tk_dirSign', x: px(73) + 8, y: px(34) + 14, solid: solidAt(73, 34, 1, 4) },
  ...[[85, 24], [81, 32], [83, 37]].map(([tx, ty], i): Decor => ({ kind: 'stoneCairn', ...mid(tx, ty), variant: i % 2 })),
  { kind: 'tk_birds', x: px(92), y: px(26), variant: 2 },
  { kind: 'tk_birds', x: px(90), y: px(38), variant: 2 },
  { kind: 'tk_birds', x: px(86), y: px(6), variant: 2 },
  { kind: 'lamp', x: px(64) + 8, y: px(41) + 14 },
  { kind: 'lamp', x: px(72) + 8, y: px(36) + 14 },
];

// --- Art pass: mist, rapids and glints on the river, hikers, butterflies, beach life -----------
const person = (tx: number, ty: number, v: number, dx = 8, dy = 12): Decor => ({ kind: 'xm_person', x: px(tx) + dx, y: px(ty) + dy, variant: v });
decor.push(
  // The gorge: river mist, white water round boulders, sparkle on the water, more cascades.
  ...[6, 26, 46, 66].map((tx, i): Decor => ({ kind: 'tk_mist', ...span(tx, 12), y: px(17) + 4, variant: i })),
  ...[14, 36, 58].map((tx, i): Decor => ({ kind: 'tk_mist', ...span(tx, 10), y: px(16) + 2, variant: 3 + i })),
  ...[2, 16, 30, 44, 58, 72].map((tx): Decor => ({ kind: 'tk_glints', ...span(tx, 12), y: px(18) + 14, h: 64 })),
  ...([[12, 16, 1], [27, 17, 2], [45, 16, 0], [60, 17, 1], [80, 16, 2]] as const).flatMap(([tx, ty, v]): Decor[] => [
    { kind: 'tk_boulder', ...mid(tx, ty), variant: v },
    { kind: 'tk_rapids', x: px(tx) + 8, y: px(ty) + 12, w: 44 },
  ]),
  { kind: 'waterfall', x: px(5) + 6, y: px(14) + 4, h: 56 },
  { kind: 'waterfall', x: px(31) + 2, y: px(15) + 4, h: 60 },
  { kind: 'waterfall', x: px(65) + 10, y: px(14) + 4, h: 50 },
  // Hikers in hard hats along the walkway and the Nine Turns trail; monkeys and butterflies.
  { kind: 'tk_hikers', x: px(10) + 8, y: px(19) + 12, w: 40, variant: 0 },
  { kind: 'tk_hikers', x: px(36) + 8, y: px(19) + 12, w: 40, variant: 1 },
  { kind: 'tk_hikers', x: px(57) + 8, y: px(19) + 12, w: 40, variant: 2 },
  { kind: 'tk_hikers', x: px(7) + 8, y: px(27) + 12, w: 40, variant: 3 },
  { kind: 'tk_hikers', x: px(18) + 8, y: px(30) + 8, w: 40, variant: 7 },
  { kind: 'tk_hikers', x: px(49) + 8, y: px(34) + 12, w: 40, variant: 4 },
  { kind: 'tk_monkey', x: px(12) + 4, y: px(24) + 10, variant: 2 },
  { kind: 'tk_monkey', x: px(47) + 6, y: px(25) + 12 },
  { kind: 'tk_monkey', x: px(18) + 2, y: px(13) + 8, variant: 1 },
  ...([[26, 27, 0], [44, 33, 1], [50, 26, 2], [70, 27, 3], [52, 30, 0], [30, 36, 1], [3, 26, 2], [40, 22, 3]] as const).map(([tx, ty, v]): Decor => ({ kind: 'tk_butterflies', x: px(tx) + 8, y: px(ty) + 12, w: 48, variant: v })),
  // The visitor centre: tourists, banners, pots, pigeons, a seated couple, bikes.
  person(58, 24, 6), person(63, 25, 13), person(60, 29, 22), person(65, 27, 1), person(57, 31, 17), person(69, 31, 30),
  { kind: 'ck_tourists', x: px(61) + 8, y: px(25) + 12, w: 60, variant: 1 },
  { kind: 'hc_banner', x: px(56) + 8, y: px(26) + 12, w: 20, variant: 1, solid: { x: px(56) + 6, y: px(26) + 9, w: 4, h: 3 } },
  { kind: 'hc_banner', x: px(65) + 8, y: px(23) + 12, w: 20, variant: 1, solid: { x: px(65) + 6, y: px(23) + 9, w: 4, h: 3 } },
  { kind: 'hc_flowerPot', x: px(57) + 8, y: px(27) + 12, w: 30, variant: 0, solid: { x: px(57) + 1, y: px(27) + 6, w: 14, h: 6 } },
  { kind: 'hc_flowerPot', x: px(64) + 8, y: px(27) + 12, w: 30, variant: 2, solid: { x: px(64) + 1, y: px(27) + 6, w: 14, h: 6 } },
  { kind: 'hc_sitters', x: px(59) + 8, y: px(31) + 12, w: 36, variant: 1, solid: { x: px(59) - 7, y: px(31) + 2, w: 30, h: 10 } },
  { kind: 'xy_pigeons', x: px(62) + 8, y: px(29) + 8, w: 40, variant: 5 },
  { kind: 'hc_puddle', x: px(60) + 8, y: px(23) + 8, w: 24 },
  { kind: 'hc_puddle', x: px(64) + 8, y: px(29) + 10, w: 22 },
  { kind: 'xy_bicycle', x: px(70) + 8, y: px(24) + 10, w: 20, variant: 1 },
  { kind: 'trashBin', x: px(58) + 8, y: px(31) + 12 },
  { kind: 'tk_hikers', x: px(67) + 8, y: px(27) + 12, w: 40, variant: 5 },
  // Buluowan and Shakadang: more ferns, butterflies, a hiker pair on the rope bridge trail.
  ...([[27, 26], [43, 27], [31, 40], [38, 41], [46, 36], [50, 29], [54, 33], [50, 40]] as const).map(([tx, ty], i): Decor => ({ kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 10, variant: i % 3 })),
  // Qixingtan: kites, anglers, people on mats, the flying-fish stall, driftwood, gulls, pots along the path.
  { kind: 'ck_kites', x: px(84) + 8, y: px(27) + 12, w: 80 },
  { kind: 'ck_kites', x: px(86) + 8, y: px(36) + 12, w: 80, variant: 1 },
  { kind: 'tk_fisherman', x: px(87) + 8, y: px(24) + 12, w: 60 },
  { kind: 'tk_fisherman', x: px(86) + 8, y: px(41) + 10, w: 60, variant: 1 },
  { kind: 'tk_beachFolk', x: px(82) + 8, y: px(31) + 12, w: 60, solid: { x: px(82) - 6, y: px(31) + 4, w: 32, h: 8 } },
  { kind: 'tk_beachFolk', x: px(85) + 8, y: px(37) + 4, w: 60, variant: 1, solid: { x: px(85) - 6, y: px(36) + 12, w: 32, h: 8 } },
  { kind: 'tk_beachFolk', x: px(81) + 8, y: px(45) + 12, w: 60, variant: 2, solid: { x: px(81) - 6, y: px(45) + 4, w: 32, h: 8 } },
  { kind: 'tk_fishStall', x: px(81) + 8, y: px(33) + 12, w: 60, solid: { x: px(80), y: px(33), w: 48, h: 12 } },
  { kind: 'tk_driftwood', x: px(85) + 8, y: px(25) + 12, w: 32 },
  { kind: 'tk_driftwood', x: px(88) + 8, y: px(42) + 8, w: 36, variant: 1 },
  { kind: 'tk_driftwood', x: px(83) + 8, y: px(21) + 12, w: 32, variant: 1 },
  person(82, 29, 3, 8, 12), person(84, 34, 11), person(88, 30, 23), person(83, 40, 35), person(80, 22, 15), person(87, 46, 43),
  { kind: 'tk_birds', x: px(88), y: px(22), variant: 2 },
  { kind: 'tk_birds', x: px(84), y: px(44), variant: 2 },
  ...([[85, 22], [88, 34], [83, 42], [89, 28]] as const).map(([tx, ty], i): Decor => ({ kind: 'stoneCairn', ...mid(tx, ty), variant: (i + 1) % 2 })),
  ...[23, 31, 47].map((ty, i): Decor => ({ kind: 'tk_palm', x: px(76) + 8, y: px(ty) + 14, variant: (i + 1) % 2, solid: { x: px(76) + 4, y: px(ty) + 9, w: 8, h: 5 } })),
  { kind: 'hc_flowerPot', x: px(77) + 8, y: px(25) + 12, w: 30, variant: 1, solid: { x: px(77) + 1, y: px(25) + 6, w: 14, h: 6 } },
  { kind: 'hc_flowerPot', x: px(77) + 8, y: px(38) + 12, w: 30, variant: 3, solid: { x: px(77) + 1, y: px(38) + 6, w: 14, h: 6 } },
  { kind: 'hc_sitters', x: px(77) + 8, y: px(44) + 12, w: 36, variant: 0, solid: { x: px(77) - 7, y: px(44) + 2, w: 30, h: 10 } },
  { kind: 'trashBin', x: px(77) + 8, y: px(35) + 12 },
  { kind: 'xy_bicycle', x: px(77) + 8, y: px(27) + 12, w: 20, variant: 2 },
  // The village: scooters in a row, laundry, pots, a dog, people.
  { kind: 'xy_scooterRow', x: px(69) + 24, y: px(40) + 14, w: 48, variant: 1, solid: { x: px(69) + 2, y: px(40) + 4, w: 44, h: 10 } },
  { kind: 'xy_laundry', x: px(70) + 8, y: px(38) + 12, w: 18 },
  { kind: 'hc_dog', x: px(63) + 8, y: px(41) + 8, w: 20 },
  { kind: 'hc_flowerPot', x: px(58) + 8, y: px(40) + 12, w: 30, variant: 0, solid: { x: px(58) + 1, y: px(40) + 6, w: 14, h: 6 } },
  { kind: 'hc_flowerPot', x: px(73) + 8, y: px(46) + 12, w: 30, variant: 2, solid: { x: px(73) + 1, y: px(46) + 6, w: 14, h: 6 } },
  person(62, 40, 9), person(67, 44, 27), person(58, 46, 14),
  { kind: 'hc_cables', x: px(60) + 8 + 64, y: px(41) + 14, w: 128, variant: 1 },
  { kind: 'hc_cables', x: px(66) + 8 + 48, y: px(47) + 10, w: 96, variant: 0 },
);

// Forest edges get real trees so the mountains read as wooded slopes, not flat green.
for (const [x0, x1, y] of [[0, 24, 32], [24, 46, 41], [0, 68, 6]] as const) {
  for (let tx = x0; tx < x1; tx += 3) {
    if (y === 6 && tx >= 38 && tx <= 46) continue;
    decor.push({ kind: (tx / 3 + y) % 3 ? 'pine' : 'tree', x: px(tx) + 8 + ((tx * 5) % 7) - 3, y: px(y) + 6 });
  }
}
// Surf along the shore and riders on the cycle path, in short segments (props are culled near their anchor).
for (let ty = 3; ty <= 47; ty += 4) if (ty < 13 || ty > 20) decor.push({ kind: 'tk_surf', x: px(shoreX(ty)), y: px(ty + 1), h: px(4) });
for (let ty = 24; ty <= 47; ty += 4) decor.push({ kind: 'tk_cyclists', x: px(79), y: px(ty), h: px(4) });
// Ferns, shrubs and the odd pine on the marble, so the cliff tops aren't bare.
for (let i = 0; i < 40; i++) {
  const tx = (i * 37) % 74, ty = 7 + ((i * 13) % 4);
  if ((tx >= 39 && tx <= 51) || tx <= 9) continue;
  decor.push(i % 7 === 0 ? { kind: 'pine', x: px(tx) + 8, y: px(ty) + 12 } : { kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 10, variant: i % 3 });
}
for (const [tx, ty] of [[2, 23], [14, 23], [26, 23], [37, 23], [47, 30], [55, 26], [46, 38], [55, 42], [3, 27], [9, 31], [18, 23], [70, 2], [77, 10], [72, 9]]) decor.push({ kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 10, variant: (tx + ty) % 3 });
// Lamps along the gorge road (they glow at night).
for (const tx of [12, 30, 48, 60]) decor.push({ kind: 'lamp', x: px(tx) + 8, y: px(19) + 12 });

const lights: Light[] = [
  ...[12, 30, 48, 60].map((tx) => ({ x: px(tx) + 8, y: px(19), r: 46, color: '255,224,150' })),
  ...[33, 41, 46].map((ty) => ({ x: px(77) + 8, y: px(ty) + 2, r: 50, color: '255,224,150' })),
  { x: px(64) + 8, y: px(41), r: 50, color: '255,224,150' },
  { x: px(72) + 8, y: px(36), r: 50, color: '255,224,150' },
  { x: px(61), y: px(27), r: 70, color: '255,236,190' }, // visitor centre windows
  { x: px(42) + 8, y: px(11) + 4, r: 36, color: '255,150,90' }, // shrine lanterns
  { x: px(50) + 8, y: px(11) + 4, r: 36, color: '255,150,90' },
  { x: px(60), y: px(43), r: 60, color: '255,170,110' }, // dance square lanterns
  { x: px(93), y: px(43), r: 40, color: '255,230,150' }, // pier lamp
  { x: px(38) + 8, y: px(33), r: 40, color: '255,150,80' }, // Buluowan fire pit
  { x: px(81), y: px(41), r: 44, color: '170,200,255' }, // star-gazing deck
  { x: px(81) + 8, y: px(31), r: 50, color: '255,200,150' }, // flying-fish stall
  { x: px(56) + 8, y: px(24), r: 40, color: '255,224,150' }, // visitor-centre banners
  { x: px(65) + 8, y: px(21), r: 40, color: '255,224,150' },
];

export const hualien: MapDef = {
  id: 'hualien',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [],
  decor,
  areas: [
    // Helmet zone first (first match wins): Cimu, the Nine Turns and the Swallow Grotto.
    { key: 'gorge', rect: { x: 0, y: 0, w: px(24), h: px(32) } },
    { key: 'gorge', rect: { x: 0, y: 0, w: px(40), h: px(22) } },
    { key: 'shrine', rect: { x: px(40), y: 0, w: px(14), h: px(22) } },
    { key: 'buluowan', rect: { x: px(24), y: px(22), w: px(23), h: px(26) } },
    { key: 'shakadang', rect: { x: px(47), y: px(22), w: px(9), h: px(26) } },
    { key: 'qingshui', rect: { x: px(68), y: 0, w: px(32), h: px(14) } },
    { key: 'beach', rect: { x: px(76), y: px(19), w: px(24), h: px(29) } },
    { key: 'village', rect: { x: px(56), y: px(35), w: px(18), h: px(13) } },
    { key: 'visitor', rect: { x: px(54), y: px(19), w: px(22), h: px(16) } },
    { key: 'taroko', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: {
    taroko: { x: px(64) + 8, y: px(31) + 8, facing: 'down' },
    qixingtan: { x: px(76) + 8, y: px(31) + 8, facing: 'right' },
  },
};
